import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Grid,
  GridItem,
  Card,
  CardBody,
  CardHeader,
  FormControl,
  FormLabel,
  Input,
  Select,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Divider,
  Icon,
  useColorModeValue,
  Table,
  Tbody,
  Tr,
  Td,
  TableContainer,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { Calculator, Leaf, Video, Users, Clock, Zap } from "lucide-react";

const CarbonEmissionCalculator = () => {
  const [duration, setDuration] = useState(60);
  const [participants, setParticipants] = useState(2);
  const [camerasOn, setCamerasOn] = useState(1);
  const [videoQuality, setVideoQuality] = useState("HD");
  const [numInterviews, setNumInterviews] = useState(1);

  const [traditionalEmission, setTraditionalEmission] = useState(0);
  const [aptofyEmission, setAptofyEmission] = useState(0);

  // Editable DATA_RATES constants
  const [dataRates, setDataRates] = useState({
    VIDEO_HD: 600,          // MB/hour (720p)
    VIDEO_FULL_HD: 1350,    // MB/hour (1080p)
    AUDIO: 15,              // MB/hour (32 kbps mono)
    IMAGE_SIZE: 0.1,        // MB (100 KB per image)
    TEXT_PER_MINUTE: 10,    // KB
    CO2_PER_MB: 0.06        // g CO₂ per MB  (60 g / GB)
  });

  const bg = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const mainTextColor = useColorModeValue("green.600", "green.400");
  const disclaimerTextColor = useColorModeValue("black", "gray.300");
  const traditionalCardBg = useColorModeValue("red.50", "red.800");
  const aptofyCardBg = useColorModeValue("green.50", "green.800");
  const savedCardBg = useColorModeValue("blue.50", "blue.800");

  // Helper: valid values for calculations (defaults if empty/invalid)
  const validDuration = duration && duration > 0 ? duration : 60;
  const validParticipants = participants && participants >= 2 ? participants : 2;
  const validCamerasOn = camerasOn && camerasOn >= 0 ? camerasOn : 1;
  const validNumInterviews = numInterviews && numInterviews >= 1 ? numInterviews : 1;

  /* =========================
     TRADITIONAL VIDEO CALL FORMULA
     =========================
     For a video call with N participants, C cameras on, duration D minutes:
     
     Video Data (MB) = C × VideoRate(MB/hour) × (D/60)
     - Each camera sends a video stream
     - VideoRate is per stream (one direction)
     - In a call, data flows: sender → network → receiver(s)
     - For carbon calculation, we count total data transferred through network
     
     Audio Data (MB) = N × AudioRate(MB/hour) × (D/60)
     - Each participant sends audio (even if camera is off)
     - AudioRate is per participant stream
     
     Total Data (MB) = Video Data + Audio Data
     Carbon Emission (g CO₂) = Total Data × CO2_PER_MB
     ========================= */
  useEffect(() => {
    const calculateTraditionalEmission = () => {
      const durationHours = validDuration / 60;

      const videoRate =
        videoQuality === "Full HD"
          ? dataRates.VIDEO_FULL_HD
          : dataRates.VIDEO_HD;

      // Video data: camerasOn streams × videoRate per stream × duration
      // Each camera sends video, which is received by other participants
      // Total network transfer = camerasOn × videoRate (per hour per stream)
      const videoDataMB =
        validCamerasOn * videoRate * durationHours;

      // Audio data: all participants send audio streams
      // Each participant sends audio (compressed) to others
      const audioDataMB =
        validParticipants * dataRates.AUDIO * durationHours;

      const totalDataMB = videoDataMB + audioDataMB;

      return totalDataMB * dataRates.CO2_PER_MB;
    };

    /* =========================
       APTOfY INTERVIEW FORMULA
       =========================
       For an Aptofy interview with duration D minutes:
       
       Image Data (MB) = (D × 60 / 10) × ImageSize(MB)
       - Images captured every 10 seconds
       - ImageCount = (D minutes × 60 sec/min) / 10 sec per image
       - Each image compressed and sent from client to server
       
       Audio Data (MB) = AudioRate(MB/hour) × (D/60)
       - Only candidate audio stream (compressed)
       - Sent from client to server
       
       Text Data (MB) = (TextPerMinute(KB) × D) / 1024
       - Text payload sent every minute
       - Convert KB to MB by dividing by 1024
       
       Total Data (MB) = Image Data + Audio Data + Text Data
       Carbon Emission (g CO₂) = Total Data × CO2_PER_MB
       ========================= */
    const calculateAptofyEmission = () => {
      const durationHours = validDuration / 60;

      // Images every 10 seconds over duration
      // Formula: (duration in minutes × 60) / 10 = number of images
      // Example: 60 min × 60 = 3600 sec / 10 = 360 images
      const imageCount = (validDuration * 60) / 10;
      const imageDataMB =
        imageCount * dataRates.IMAGE_SIZE;

      // Only candidate audio stream (compressed)
      // AudioRate is MB per hour, so multiply by duration in hours
      const audioDataMB =
        dataRates.AUDIO * durationHours;

      // Text payload: 10 KB per minute (as specified)
      // Convert KB to MB: divide by 1024
      // Formula: (TextPerMinute × duration) / 1024
      const textDataMB =
        (dataRates.TEXT_PER_MINUTE * validDuration) / 1024;

      const totalDataMB =
        imageDataMB + audioDataMB + textDataMB;

      return totalDataMB * dataRates.CO2_PER_MB;
    };

    setTraditionalEmission(calculateTraditionalEmission());
    setAptofyEmission(calculateAptofyEmission());
  }, [validDuration, validParticipants, validCamerasOn, videoQuality, validNumInterviews, dataRates]);

  const totalTraditional = traditionalEmission * validNumInterviews;
  const totalAptofy = aptofyEmission * validNumInterviews;

  const saved = totalTraditional - totalAptofy;
  const percentageSaved =
    totalTraditional > 0
      ? ((saved / totalTraditional) * 100).toFixed(1)
      : 0;

  return (
    <Box bg={bg} minH="100vh" py={4}>
      <Container maxW="6xl">
        <VStack spacing={2} textAlign="center" mb={4}>
          <Heading size="lg">
            Calculate your organisations talent evaluation carbon footprint
          </Heading>
        </VStack>

        <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={4}>
          {/* INPUTS */}
          <Card bg={cardBg} borderColor={borderColor}>
            <CardHeader pb={2}>
              <Heading size="sm">Interview Parameters</Heading>
            </CardHeader>
            <CardBody pt={0}>
              <VStack spacing={3}>
                <FormControl>
                  <FormLabel fontSize="sm" mb={1}>Duration (minutes)</FormLabel>
                  <Input
                    type="number"
                    size="sm"
                    value={duration || ''}
                    min={1}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || val === null || val === undefined) {
                        setDuration('');
                      } else {
                        const numVal = Number(val);
                        if (!isNaN(numVal)) {
                          setDuration(numVal >= 1 ? numVal : '');
                        }
                      }
                    }}
                    onBlur={(e) => {
                      const val = e.target.value;
                      if (val === '' || val === null || val === undefined || Number(val) < 1 || isNaN(Number(val))) {
                        setDuration(60);
                      }
                    }}
                    placeholder="Type or clear to edit (default: 60)"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" mb={1}>Participants</FormLabel>
                  <Input
                    type="number"
                    size="sm"
                    value={participants || ''}
                    min={2}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || val === null || val === undefined) {
                        setParticipants('');
                      } else {
                        const numVal = Number(val);
                        if (!isNaN(numVal)) {
                          setParticipants(numVal >= 2 ? numVal : '');
                        }
                      }
                    }}
                    onBlur={(e) => {
                      const val = e.target.value;
                      if (val === '' || val === null || val === undefined || Number(val) < 2 || isNaN(Number(val))) {
                        setParticipants(2);
                      }
                    }}
                    placeholder="Type or clear to edit (min: 2)"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" mb={1}>Cameras On</FormLabel>
                  <Input
                    type="number"
                    size="sm"
                    value={camerasOn || ''}
                    min={0}
                    max={validParticipants}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || val === null || val === undefined) {
                        setCamerasOn('');
                      } else {
                        const numVal = Number(val);
                        if (!isNaN(numVal)) {
                          setCamerasOn(numVal >= 0 ? numVal : '');
                        }
                      }
                    }}
                    onBlur={(e) => {
                      const val = e.target.value;
                      if (val === '' || val === null || val === undefined || Number(val) < 0 || isNaN(Number(val))) {
                        setCamerasOn(1);
                      }
                    }}
                    placeholder="Type or clear to edit (min: 0)"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" mb={1}>Video Quality</FormLabel>
                  <Select size="sm" value={videoQuality} onChange={(e) => setVideoQuality(e.target.value)}>
                    <option value="HD">HD (720p)</option>
                    <option value="Full HD">Full HD (1080p)</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" mb={1}>Number of Interviews</FormLabel>
                  <Input
                    type="number"
                    size="sm"
                    value={numInterviews || ''}
                    min={1}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || val === null || val === undefined) {
                        setNumInterviews('');
                      } else {
                        const numVal = Number(val);
                        if (!isNaN(numVal)) {
                          setNumInterviews(numVal >= 1 ? numVal : '');
                        }
                      }
                    }}
                    onBlur={(e) => {
                      const val = e.target.value;
                      if (val === '' || val === null || val === undefined || Number(val) < 1 || isNaN(Number(val))) {
                        setNumInterviews(1);
                      }
                    }}
                    placeholder="Type or clear to edit (min: 1)"
                  />
                </FormControl>

                {/* <Divider my={2} />

                <Heading size="xs" width="100%" textAlign="left">Data Rate Constants (Editable)</Heading>
                
                <FormControl>
                  <FormLabel fontSize="sm" mb={1}>Video HD Rate (MB/hour)</FormLabel>
                  <Input
                    type="number"
                    size="sm"
                    value={dataRates.VIDEO_HD}
                    min={0}
                    step={0.01}
                    onChange={(e) => setDataRates({...dataRates, VIDEO_HD: Number(e.target.value) || 0})}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" mb={1}>Video Full HD Rate (MB/hour)</FormLabel>
                  <Input
                    type="number"
                    size="sm"
                    value={dataRates.VIDEO_FULL_HD}
                    min={0}
                    step={0.01}
                    onChange={(e) => setDataRates({...dataRates, VIDEO_FULL_HD: Number(e.target.value) || 0})}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" mb={1}>Audio Rate (MB/hour)</FormLabel>
                  <Input
                    type="number"
                    size="sm"
                    value={dataRates.AUDIO}
                    min={0}
                    step={0.01}
                    onChange={(e) => setDataRates({...dataRates, AUDIO: Number(e.target.value) || 0})}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" mb={1}>Image Size (MB)</FormLabel>
                  <Input
                    type="number"
                    size="sm"
                    value={dataRates.IMAGE_SIZE}
                    min={0}
                    step={0.001}
                    onChange={(e) => setDataRates({...dataRates, IMAGE_SIZE: Number(e.target.value) || 0})}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" mb={1}>Text per Minute (KB)</FormLabel>
                  <Input
                    type="number"
                    size="sm"
                    value={dataRates.TEXT_PER_MINUTE}
                    min={0}
                    step={0.01}
                    onChange={(e) => setDataRates({...dataRates, TEXT_PER_MINUTE: Number(e.target.value) || 0})}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" mb={1}>CO₂ per MB (g CO₂/MB)</FormLabel>
                  <Input
                    type="number"
                    size="sm"
                    value={dataRates.CO2_PER_MB}
                    min={0}
                    step={0.0001}
                    onChange={(e) => setDataRates({...dataRates, CO2_PER_MB: Number(e.target.value) || 0})}
                  />
                </FormControl> */}
              </VStack>
            </CardBody>
          </Card>

          {/* RESULTS */}
          <VStack spacing={3} align="stretch">
            <Card bg={traditionalCardBg} size="sm">
              <CardBody p={3}>
                <Stat>
                  <StatLabel fontSize="xs">Traditional Video Interview</StatLabel>
                  <StatNumber fontSize="lg">{totalTraditional.toFixed(2)} g CO₂</StatNumber>
                  <StatHelpText fontSize="xs">
                    Data: {((traditionalEmission / dataRates.CO2_PER_MB) * validNumInterviews).toFixed(2)} MB
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card bg={aptofyCardBg} size="sm">
              <CardBody p={3}>
                <Stat>
                  <StatLabel fontSize="xs" display="flex" alignItems="center" gap={1}>
                    <Icon as={Leaf} boxSize={3} /> Aptofy Interview
                  </StatLabel>
                  <StatNumber fontSize="lg">{totalAptofy.toFixed(2)} g CO₂</StatNumber>
                  <StatHelpText fontSize="xs">
                    Data: {((aptofyEmission / dataRates.CO2_PER_MB) * validNumInterviews).toFixed(2)} MB
                  </StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            <Card bg={savedCardBg} size="sm">
              <CardBody p={3} textAlign="center">
                <Stat>
                  <StatLabel fontSize="xs">Carbon Emission Reduced</StatLabel>
                  <StatNumber fontSize="lg">{saved.toFixed(2)} g CO₂</StatNumber>
                  <StatHelpText fontSize="xs">{percentageSaved}% reduction</StatHelpText>
                </Stat>
              </CardBody>
            </Card>

            {/* Calculation Breakdown */}
            {/* <Card bg={cardBg} size="sm" mt={2}>
              <CardHeader pb={2}>
                <Heading size="xs">Calculation Breakdown</Heading>
              </CardHeader>
              <CardBody pt={0} fontSize="xs">
                <VStack spacing={2} align="stretch">
                  <Box>
                    <Text fontWeight="bold" mb={1}>Traditional Video Call:</Text>
                    <Text>Video: {validCamerasOn} camera(s) × {videoQuality === "Full HD" ? dataRates.VIDEO_FULL_HD : dataRates.VIDEO_HD} MB/h × {(validDuration/60).toFixed(2)} h = {((validCamerasOn * (videoQuality === "Full HD" ? dataRates.VIDEO_FULL_HD : dataRates.VIDEO_HD) * validDuration / 60) * numInterviews).toFixed(2)} MB</Text>
                    <Text>Audio: {validParticipants} participant(s) × {dataRates.AUDIO} MB/h × {(validDuration/60).toFixed(2)} h = {((validParticipants * dataRates.AUDIO * validDuration / 60) * numInterviews).toFixed(2)} MB</Text>
                  </Box>
                  <Box>
                    <Text fontWeight="bold" mb={1}>Aptofy Interview:</Text>
                    <Text>Images: {((validDuration * 60) / 10).toFixed(0)} images × {dataRates.IMAGE_SIZE} MB = {((((validDuration * 60) / 10) * dataRates.IMAGE_SIZE) * numInterviews).toFixed(2)} MB</Text>
                    <Text>Audio: {dataRates.AUDIO} MB/h × {(validDuration/60).toFixed(2)} h = {((dataRates.AUDIO * validDuration / 60) * numInterviews).toFixed(2)} MB</Text>
                    <Text>Text: {dataRates.TEXT_PER_MINUTE} KB/min × {validDuration} min / 1024 = {(((dataRates.TEXT_PER_MINUTE * validDuration) / 1024) * numInterviews).toFixed(2)} MB</Text>
                  </Box>
                </VStack>
              </CardBody>
            </Card> */}
          </VStack>
        </Grid>

        <Box mt={6} textAlign="center" px={{ base: 4, md: 8 }} py={6}>
          <Text 
            fontSize={{ base: "lg", md: "xl", lg: "2xl" }} 
            fontWeight="semibold" 
            color={mainTextColor}
            lineHeight="tall"
          >
            Make your hiring process up to {percentageSaved}% lighter on the planet—switch to Aptofy's intelligent interviews that cut data, carbon, and clutter without compromising insight.
          </Text>
        </Box>

        <Box mt={8} mb={4} textAlign="center">
          <Text fontSize={{ base: "xs", md: "sm" }} color={disclaimerTextColor} opacity={0.7}>
            Estimates based on industry-average data transfer and global carbon factors.
          </Text>
        </Box>

        {/* ============================================
            CALCULATIONS & EQUATIONS REFERENCE
            (Comment out this section before production)
            ============================================ */}
        {/* <Card bg={cardBg} mt={6} borderColor={borderColor}>
          <CardHeader pb={2}>
            <Heading size="sm" color="orange.500">
              📐 Calculations & Equations Reference
            </Heading>
            <Text fontSize="xs" color="gray.500" mt={1}>
              Review all formulas and calculations used in this calculator
            </Text>
          </CardHeader>
          <CardBody pt={0}>
            <VStack spacing={4} align="stretch">
              {/* Constants Section */}
              {/* <Box>
                <Text fontWeight="bold" mb={2} fontSize="sm" color="blue.600">
                  Constants (Default Values)
                </Text>
                <VStack spacing={1} align="stretch" fontSize="xs" pl={2}>
                  <Text>VIDEO_HD = {dataRates.VIDEO_HD} MB/hour (720p)</Text>
                  <Text>VIDEO_FULL_HD = {dataRates.VIDEO_FULL_HD} MB/hour (1080p)</Text>
                  <Text>AUDIO = {dataRates.AUDIO} MB/hour (32 kbps mono)</Text>
                  <Text>IMAGE_SIZE = {dataRates.IMAGE_SIZE} MB (100 KB per image)</Text>
                  <Text>TEXT_PER_MINUTE = {dataRates.TEXT_PER_MINUTE} KB</Text>
                  <Text>CO2_PER_MB = {dataRates.CO2_PER_MB} g CO₂/MB (60 g/GB)</Text>
                </VStack>
              </Box>

              <Divider />

              {/* Traditional Video Call Section */}
              {/* <Box>
                <Text fontWeight="bold" mb={2} fontSize="sm" color="red.600">
                  1. Traditional Video Call Calculation
                </Text>
                <VStack spacing={2} align="stretch" fontSize="xs" pl={2}>
                  <Box>
                    <Text fontWeight="semibold" mb={1}>Variables:</Text>
                    <Text pl={2}>N = Number of participants = {validParticipants}</Text>
                    <Text pl={2}>C = Number of cameras on = {validCamerasOn}</Text>
                    <Text pl={2}>D = Duration in minutes = {validDuration}</Text>
                    <Text pl={2}>VideoRate = {videoQuality === "Full HD" ? dataRates.VIDEO_FULL_HD : dataRates.VIDEO_HD} MB/hour ({videoQuality})</Text>
                  </Box>
                  <Box>
                    <Text fontWeight="semibold" mb={1}>Equations:</Text>
                    <Text pl={2} fontFamily="mono">Duration Hours = D / 60 = {validDuration} / 60 = {(validDuration/60).toFixed(2)} hours</Text>
                    <Text pl={2} fontFamily="mono" mt={1}>Video Data (MB) = C × VideoRate × (D / 60)</Text>
                    <Text pl={4} fontFamily="mono">= {validCamerasOn} × {videoQuality === "Full HD" ? dataRates.VIDEO_FULL_HD : dataRates.VIDEO_HD} × {(validDuration/60).toFixed(2)}</Text>
                    <Text pl={4} fontFamily="mono">= {((validCamerasOn * (videoQuality === "Full HD" ? dataRates.VIDEO_FULL_HD : dataRates.VIDEO_HD) * validDuration / 60)).toFixed(2)} MB</Text>
                    <Text pl={2} fontFamily="mono" mt={1}>Audio Data (MB) = N × AUDIO × (D / 60)</Text>
                    <Text pl={4} fontFamily="mono">= {validParticipants} × {dataRates.AUDIO} × {(validDuration/60).toFixed(2)}</Text>
                    <Text pl={4} fontFamily="mono">= {((validParticipants * dataRates.AUDIO * validDuration / 60)).toFixed(2)} MB</Text>
                    <Text pl={2} fontFamily="mono" mt={1}>Total Data (MB) = Video Data + Audio Data</Text>
                    <Text pl={4} fontFamily="mono">= {((validCamerasOn * (videoQuality === "Full HD" ? dataRates.VIDEO_FULL_HD : dataRates.VIDEO_HD) * validDuration / 60)).toFixed(2)} + {((validParticipants * dataRates.AUDIO * validDuration / 60)).toFixed(2)}</Text>
                    <Text pl={4} fontFamily="mono">= {((validCamerasOn * (videoQuality === "Full HD" ? dataRates.VIDEO_FULL_HD : dataRates.VIDEO_HD) * validDuration / 60) + (validParticipants * dataRates.AUDIO * validDuration / 60)).toFixed(2)} MB</Text>
                    <Text pl={2} fontFamily="mono" mt={1}>Carbon Emission (g CO₂) = Total Data × CO2_PER_MB</Text>
                    <Text pl={4} fontFamily="mono">= {((validCamerasOn * (videoQuality === "Full HD" ? dataRates.VIDEO_FULL_HD : dataRates.VIDEO_HD) * validDuration / 60) + (validParticipants * dataRates.AUDIO * validDuration / 60)).toFixed(2)} × {dataRates.CO2_PER_MB}</Text>
                    <Text pl={4} fontFamily="mono">= {traditionalEmission.toFixed(2)} g CO₂ (per interview)</Text>
                    <Text pl={2} fontFamily="mono" mt={1}>Total Carbon (for {numInterviews} interview(s)) = {traditionalEmission.toFixed(2)} × {numInterviews} = {totalTraditional.toFixed(2)} g CO₂</Text>
                  </Box>
                </VStack>
              </Box>

              <Divider />

              {/* Aptofy Interview Section */}
              {/* <Box>
                <Text fontWeight="bold" mb={2} fontSize="sm" color="green.600">
                  2. Aptofy Interview Calculation
                </Text>
                <VStack spacing={2} align="stretch" fontSize="xs" pl={2}>
                  <Box>
                    <Text fontWeight="semibold" mb={1}>Variables:</Text>
                    <Text pl={2}>D = Duration in minutes = {validDuration}</Text>
                  </Box>
                  <Box>
                    <Text fontWeight="semibold" mb={1}>Equations:</Text>
                    <Text pl={2} fontFamily="mono">Duration Hours = D / 60 = {validDuration} / 60 = {(validDuration/60).toFixed(2)} hours</Text>
                    <Text pl={2} fontFamily="mono" mt={1}>Image Count = (D × 60) / 10</Text>
                    <Text pl={4} fontFamily="mono">= ({validDuration} × 60) / 10</Text>
                    <Text pl={4} fontFamily="mono">= {((validDuration * 60) / 10).toFixed(0)} images</Text>
                    <Text pl={2} fontFamily="mono" mt={1}>Image Data (MB) = Image Count × IMAGE_SIZE</Text>
                    <Text pl={4} fontFamily="mono">= {((validDuration * 60) / 10).toFixed(0)} × {dataRates.IMAGE_SIZE}</Text>
                    <Text pl={4} fontFamily="mono">= {((((validDuration * 60) / 10) * dataRates.IMAGE_SIZE)).toFixed(2)} MB</Text>
                    <Text pl={2} fontFamily="mono" mt={1}>Audio Data (MB) = AUDIO × (D / 60)</Text>
                    <Text pl={4} fontFamily="mono">= {dataRates.AUDIO} × {(validDuration/60).toFixed(2)}</Text>
                    <Text pl={4} fontFamily="mono">= {((dataRates.AUDIO * validDuration / 60)).toFixed(2)} MB</Text>
                    <Text pl={2} fontFamily="mono" mt={1}>Text Data (MB) = (TEXT_PER_MINUTE × D) / 1024</Text>
                    <Text pl={4} fontFamily="mono">= ({dataRates.TEXT_PER_MINUTE} × {validDuration}) / 1024</Text>
                    <Text pl={4} fontFamily="mono">= {(((dataRates.TEXT_PER_MINUTE * validDuration) / 1024)).toFixed(2)} MB</Text>
                    <Text pl={2} fontFamily="mono" mt={1}>Total Data (MB) = Image Data + Audio Data + Text Data</Text>
                    <Text pl={4} fontFamily="mono">= {((((validDuration * 60) / 10) * dataRates.IMAGE_SIZE)).toFixed(2)} + {((dataRates.AUDIO * validDuration / 60)).toFixed(2)} + {(((dataRates.TEXT_PER_MINUTE * validDuration) / 1024)).toFixed(2)}</Text>
                    <Text pl={4} fontFamily="mono">= {((((validDuration * 60) / 10) * dataRates.IMAGE_SIZE) + (dataRates.AUDIO * validDuration / 60) + ((dataRates.TEXT_PER_MINUTE * validDuration) / 1024)).toFixed(2)} MB</Text>
                    <Text pl={2} fontFamily="mono" mt={1}>Carbon Emission (g CO₂) = Total Data × CO2_PER_MB</Text>
                    <Text pl={4} fontFamily="mono">= {((((validDuration * 60) / 10) * dataRates.IMAGE_SIZE) + (dataRates.AUDIO * validDuration / 60) + ((dataRates.TEXT_PER_MINUTE * validDuration) / 1024)).toFixed(2)} × {dataRates.CO2_PER_MB}</Text>
                    <Text pl={4} fontFamily="mono">= {aptofyEmission.toFixed(2)} g CO₂ (per interview)</Text>
                    <Text pl={2} fontFamily="mono" mt={1}>Total Carbon (for {numInterviews} interview(s)) = {aptofyEmission.toFixed(2)} × {numInterviews} = {totalAptofy.toFixed(2)} g CO₂</Text>
                  </Box>
                </VStack>
              </Box>

              <Divider />

              {/* Carbon Savings Section */}
              {/* <Box>
                <Text fontWeight="bold" mb={2} fontSize="sm" color="purple.600">
                  3. Carbon Emission Reduction Calculation
                </Text>
                <VStack spacing={1} align="stretch" fontSize="xs" pl={2}>
                  <Text fontFamily="mono">Carbon Emission Reduced (g CO₂) = Total Traditional - Total Aptofy</Text>
                  <Text pl={4} fontFamily="mono">= {totalTraditional.toFixed(2)} - {totalAptofy.toFixed(2)}</Text>
                  <Text pl={4} fontFamily="mono">= {saved.toFixed(2)} g CO₂</Text>
                  <Text fontFamily="mono" mt={1}>Percentage Reduced (%) = (Carbon Emission Reduced / Total Traditional) × 100</Text>
                  <Text pl={4} fontFamily="mono">= ({saved.toFixed(2)} / {totalTraditional.toFixed(2)}) × 100</Text>
                  <Text pl={4} fontFamily="mono">= {percentageSaved}%</Text>
                </VStack>
              </Box>

              <Divider />

              {/* Notes Section */}
              {/* <Box>
                <Text fontWeight="bold" mb={2} fontSize="sm" color="gray.600">
                  Important Notes
                </Text>
                <VStack spacing={1} align="stretch" fontSize="xs" pl={2}>
                  <Text>• Images are captured every 10 seconds</Text>
                  <Text>• Audio is a single candidate stream (not per participant)</Text>
                  <Text>• Text payload is sent every minute</Text>
                  <Text>• KB to MB conversion: divide by 1024</Text>
                  <Text>• Each camera sends a video stream independently</Text>
                  <Text>• All participants send audio streams (even if camera is off)</Text>
                </VStack>
              </Box>
            </VStack>
          </CardBody>
        </Card> */}
        {/* ============================================
            END OF CALCULATIONS & EQUATIONS REFERENCE
            ============================================ */}

      </Container>
    </Box>
  );
};

export default CarbonEmissionCalculator;
